// <copyright file="auth-service.ts" company="BD">
// Copyright (c) BD. All rights reserved.
// Created: 11/17/2025 by Pankaj Mishra
// Updated: 02/16/2026 - Updated to use EnvironmentResolverService
// </copyright>

import { inject, Injectable } from "@angular/core";
import { WINDOW } from "./window-token";
import { MobileSoftwareUpgradeApiService } from "../../core/http/apiservice";
import { mobileSoftwareUpgradeEndPoint } from "../../../assets/data/softwareupgradeconstant";
import { BehaviorSubject, catchError, EMPTY, finalize, map, Observable, throwError } from "rxjs";
import { HttpErrorResponse, HttpHeaders, HttpResponse } from "@angular/common/http";
import { ApiVersionService } from "../../core/http/api-version.service";
import { AuthTokenStore } from "../../shared/stores/auth-token-store";
import { CommonAPiService } from "../../shared/services/common-service";
import { Router } from "@angular/router";
import { TokenRefreshService } from "./token-refresh.service";
import { ApiUrlConfigurationService } from "../../core/config/api-url-configuration.service";
import { LoginInfo, ValidateUserResponse } from "../../shared/model/app-data.model";
import { MatDialog } from "@angular/material/dialog";
import { UserState } from "../../shared/model/card.model";
import { authorizationEndPoint } from "../../../assets/data/apiconstant";

@Injectable({
  providedIn: "root"
})

// Defines AuthService.
export class AuthService {
  private readonly windowRef = inject(WINDOW);
  private versionService = inject(ApiVersionService);
  private apiService = inject(MobileSoftwareUpgradeApiService);
  private authTokenStore = inject(AuthTokenStore);
  private commonApiService = inject(CommonAPiService);
  private tokenRefreshService = inject(TokenRefreshService);
  private apiUrlConfig = inject(ApiUrlConfigurationService);
  private loggingOut$ = new BehaviorSubject<boolean>(false);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private version = this.versionService.getVersion();

  // Redirect user to external SSO provider with email parameter
  redirectToSSO(authURL: string): void {
    this.windowRef.location.replace(authURL);
  }
  // Validate user email exists in the system and check permissions
  vaidateUser(loginInfoObj: LoginInfo): Observable<ValidateUserResponse> {
    return this.apiService
      .post<
        ValidateUserResponse,
        LoginInfo
      >(mobileSoftwareUpgradeEndPoint.userManagementAPI.ValidateAndAuthorizeUser, loginInfoObj)
      .pipe(
        map((response: ValidateUserResponse) => response),
        catchError((err: HttpErrorResponse) => {
          return throwError(() => err);
        })
      );
  }
  getCustomToken(useState:UserState):Observable<HttpResponse<UserState>>{
    const url = authorizationEndPoint.customToken;
    return this.apiService.post<UserState, UserState>(url, useState,{observe:'response'});
  }
  // Logout user - prevent multiple simultaneous logout requests
  logout(): void {
    // Guard against concurrent logout calls
    if (this.loggingOut$.value) {
      return;
    }
    this.loggingOut$.next(true);

    // Stop token refresh timer immediately on logout
    this.tokenRefreshService.stopRefreshTimer();

    this.apiService
      .post<void, string>(mobileSoftwareUpgradeEndPoint.userManagementAPI.logOutUser, "")
      .pipe(
        catchError(() => EMPTY),
        finalize(() => {
          this.forceFrontendLogout();
          this.loggingOut$.next(false);
        })
      )
      .subscribe();
  }
  private forceFrontendLogout(): void {
    this.cleanUpSessionStorage();
    this.dialog.closeAll();
    this.router.navigateByUrl("/login");
  }
  cleanUpSessionStorage() {
    this.authTokenStore.clear();
    sessionStorage.clear();
  }
}
